import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Chip,
  Stack,
  Switch,
  Divider,
  Typography,
  Avatar,
  Autocomplete,
  Grid,
  TextField,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";

const moderators = [
  "RDIUP",
  "FTK",
  "UKH",
  "NURO",
  "IRCCS-AOUBO",
  "UoM",
  "MoM",
  "CIP",
  "NION",
  "WIZ",
  "SWPS",
  "INT",
  "UoE",
  "HWU",
  "UNIBO",
];

export default function Project() {
  const [project, setProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [color, setColor] = useState("green");
  const [isActive, setIsActive] = useState(true); // State to toggle active/inactive status
  const [editedMembers, setEditedMembers] = useState([]);
  const [editedModerator, setEditedModerator] = useState("");
  const theme = useTheme();

  const location = useLocation();
  const { state } = location;
  const projectName = state?.name; // Access the project name

  // Function to fetch project by name
  const fetchProjectByName = async (name) => {
    try {
      const response = await fetch(
        ` https://eutracker.rdiup.com/v1/projects/?name=${projectName}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const projectData = data[0];
          setProject(projectData);
          setEditedMembers(projectData.members || []);
          setEditedModerator(projectData.moderator || "");
          setColor(projectData.isActive ? "green" : "gray");
          setIsActive(projectData.isActive);
        } else {
          console.error("Project not found");
        }
      } else {
        console.error("Failed to fetch project");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
    }
  };

  useEffect(() => {
    fetchProjectByName("Smile");
  }, []);

  const handleSwitchChange = (event) => {
    setIsActive(event.target.checked);
    setColor(event.target.checked ? "green" : "gray");
  };
  const handleSave = async () => {
    if (project) {
      try {
        const response = await fetch(
          ` https://eutracker.rdiup.com/v1/projects/${project.id}/update/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              members: editedMembers,
              moderator: editedModerator,
            }),
          }
        );
        if (response.ok) {
          const updatedProject = await response.json();
          console.log("response", updatedProject);

          setProject(updatedProject);
          setIsEditing(false);
        } else {
          console.error("Failed to update project");
        }
      } catch (error) {
        console.error("Error updating project:", error);
      }
    }
  };

  if (!project) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ width: "100%", mt: 4 }}>
      <Card elevation={3} sx={{ width: "100%" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                src={project.imageURL || "default-logo-url.jpg"}
                alt="Project Logo"
                sx={{ width: 40, height: 40, mr: 2 }}
              />
              <Typography variant="h6" component="div">
                {project.name}
              </Typography>
            </Box>
            <Divider />
            <br />
            <Box>
              <Typography
                sx={{ textAlign: "left", mb: 2 }}
                color="text.secondary"
              >
                Organisation ID: <b>{project.organisation_id}</b>
              </Typography>
              <Typography
                sx={{ textAlign: "left", mb: 2 }}
                color="text.secondary"
              >
                Organisation Name: <b>{project.organisation_name}</b>
              </Typography>
              <Typography
                sx={{ textAlign: "left", mb: 2 }}
                color="text.secondary"
              >
                Coordinator: <b>{project.coordinator}</b>
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{ textAlign: "left", mb: 1 }}
                    color="text.secondary"
                  >
                    Moderator:
                  </Typography>
                  <Autocomplete
                    disabled={!isEditing} // Enable or disable based on edit mode
                    disablePortal
                    options={moderators}
                    size="small"
                    fullWidth
                    value={editedModerator}
                    onChange={(event, newValue) => setEditedModerator(newValue)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{ textAlign: "left", mb: 1 }}
                    color="text.secondary"
                  >
                    Members:
                  </Typography>
                  <Autocomplete
                    multiple
                    disabled={!isEditing} // Enable or disable based on edit mode
                    disablePortal
                    options={moderators}
                    size="small"
                    fullWidth
                    value={editedMembers}
                    onChange={(event, newValue) => setEditedMembers(newValue)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (isEditing) handleSave();
                      setIsEditing(!isEditing); // Toggle edit mode
                    }}
                  >
                    {isEditing ? "Save" : "Edit"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
          <Chip
            sx={{
              backgroundColor: color,
              color: "#fff",
              borderRadius: "4px",
              fontWeight: "bold",
              alignSelf: "flex-start",
            }}
            size="small"
            label={isActive ? "Active Project" : "Inactive Project"}
          />
        </Box>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, bgcolor: theme.palette.background.paper }}
        >
          <Typography variant="body2" color="text.secondary">
            {isActive ? "Active Project" : "Inactive Project"}
          </Typography>
          <Switch
            checked={isActive} // Set switch status based on isActive state
            onChange={handleSwitchChange} // Handle switch changes
          />
        </Stack>
      </Card>
    </Box>
  );
}
